import React, { useEffect, useId, useRef, useState } from 'react';
import { Box, Button, Dialog, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { authApi } from '../../services/authApi';
import { tokens } from '../../theme/tokens';
import FinMark from '../brand/FinMark';
import type { OnboardingPreferences } from './preferenceOptions';
import WelcomeStep from './steps/WelcomeStep';
import LinkStep from './steps/LinkStep';

export type { OnboardingPreferences } from './preferenceOptions';

interface OnboardingWizardProps {
  open: boolean;
  onComplete: () => void;
}

const STEPS = ['welcome', 'link'] as const;
const LAST = STEPS.length - 1;

const sameSettings = (a: OnboardingPreferences, b: OnboardingPreferences) =>
  a.language === b.language && a.timezone === b.timezone && a.defaultCurrency === b.defaultCurrency;

/**
 * The first-run tutorial, and the one Ajustes replays: Fin says hello and
 * Telegram gets linked right here. From there the "Primeiros passos" on
 * Início lead the way, one real step at a time. Full screen on a phone, a
 * large dialog on a computer.
 */
const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ open, onComplete }) => {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { state, applyUser } = useAuth();
  const { changeLanguage } = useLanguage();
  const user = state.user;
  const isLinked = Boolean(user?.isTelegramLinked);
  const firstName = user?.firstName ? user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1) : undefined;
  const titleId = useId();
  const contentRef = useRef<HTMLDivElement>(null);

  const [step, setStep] = useState(0);
  const initial = useRef<OnboardingPreferences>({
    language: user?.language || 'pt',
    timezone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
    defaultCurrency: user?.defaultCurrency || 'BRL',
  });
  const [preferences, setPreferences] = useState<OnboardingPreferences>(initial.current);

  // Each step starts at its top, and its title is what a screen reader hears next.
  useEffect(() => {
    if (!open) return;
    contentRef.current?.scrollTo?.({ top: 0 });
    const title = document.getElementById(titleId);
    title?.setAttribute('tabindex', '-1');
    title?.focus({ preventScroll: true });
  }, [step, open, titleId]);

  const changePreferences = (next: OnboardingPreferences) => {
    if (next.language !== preferences.language) changeLanguage(next.language);
    setPreferences(next);
  };

  const savePreferences = async () => {
    if (sameSettings(preferences, initial.current)) return;
    try {
      applyUser(await authApi.updateProfile({ ...preferences }));
      initial.current = preferences;
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  };

  const goTo = async (next: number) => {
    if (step === 0) await savePreferences();
    setStep(Math.max(0, Math.min(LAST, next)));
  };

  /** Marks the tutorial as seen and closes it; then goes to Início, if asked. */
  const finish = async (path?: string) => {
    if (step === 0) await savePreferences();
    try {
      applyUser(await authApi.updateProfile({ onboardingCompleted: true }));
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
    onComplete();
    setStep(0);
    if (path) navigate(path);
  };

  const stepName = t(`steps.${STEPS[step]}`);

  const content = (() => {
    switch (STEPS[step]) {
      case 'welcome':
        return <WelcomeStep titleId={titleId} firstName={firstName} preferences={preferences} onPreferencesChange={changePreferences} />;
      default:
        return <LinkStep titleId={titleId} />;
    }
  })();

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      disableEscapeKeyDown
      aria-labelledby={titleId}
      PaperProps={{
        sx: fullScreen
          ? { borderRadius: 0, display: 'flex', flexDirection: 'column' }
          : { width: 920, maxWidth: 'calc(100vw - 48px)', height: 'min(800px, calc(100vh - 48px))', maxHeight: 'none', borderRadius: '26px', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
      }}
    >
      {/* top: brand, progress, skip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.75, md: 2.5 }, px: { xs: 2.25, md: 3.5 }, pt: { xs: 2, md: 2.5 }, pb: { xs: 1.5, md: 2.5 }, borderBottom: { md: `1px solid ${tokens.line}` } }}>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, fontFamily: tokens.display, fontWeight: 700, fontSize: 17 }}>
          <FinMark size={26} />
          Financy
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box
            role='progressbar'
            aria-valuemin={1}
            aria-valuemax={STEPS.length}
            aria-valuenow={step + 1}
            aria-valuetext={t('wizard.progress', { step: step + 1, total: STEPS.length, name: stepName })}
            sx={{ display: 'grid', gridTemplateColumns: `repeat(${STEPS.length}, 1fr)`, gap: '6px' }}
          >
            {STEPS.map((key, index) => (
              <Box key={key} sx={{ height: 6, borderRadius: 99, bgcolor: index <= step ? tokens.accent : tokens.line, transition: 'background-color .2s' }} />
            ))}
          </Box>
          <Box component='span' sx={{ display: { xs: 'none', md: 'block' }, fontSize: 12.5, color: tokens.ink3 }}>
            {t('wizard.progress', { step: step + 1, total: STEPS.length, name: stepName })}
          </Box>
        </Box>
        {step < LAST ? (
          <Button variant='text' onClick={() => finish()} sx={{ color: tokens.ink2, minHeight: 44 }}>
            {t('wizard.skip')}
          </Button>
        ) : (
          <Box sx={{ width: 64 }} />
        )}
      </Box>

      {/* the step */}
      <Box ref={contentRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2.5, md: 5 }, py: { xs: 1.5, md: 4 } }}>
        {content}
      </Box>

      {/* bottom: back, later, continue */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: { xs: 2.25, md: 3.5 },
          pt: 2,
          pb: { xs: 'calc(16px + env(safe-area-inset-bottom))', md: 2 },
          borderTop: `1px solid ${tokens.line}`,
          bgcolor: tokens.surface,
        }}
      >
        {step > 0 ? (
          <Button variant='text' startIcon={<BackIcon />} onClick={() => goTo(step - 1)} sx={{ color: tokens.ink2, minHeight: 44 }}>
            {t('wizard.back')}
          </Button>
        ) : (
          <span />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: { xs: 1, md: 'none' }, justifyContent: 'flex-end' }}>
          {step === 0 && (
            <Button variant='contained' onClick={() => goTo(1)} sx={{ minHeight: 48, px: 3, flex: { xs: 1, md: 'none' } }}>
              {t('wizard.start')}
            </Button>
          )}
          {step === LAST && !isLinked && (
            // Telegram can wait: the first steps on Início keep it on the list.
            <Button variant='text' onClick={() => finish('/dashboard')} sx={{ color: tokens.ink2, minHeight: 44 }}>
              {t('wizard.later')}
            </Button>
          )}
          {step === LAST && isLinked && (
            // "Ir para o Início" goes there, also when Ajustes replays the tutorial.
            <Button variant='contained' onClick={() => finish('/dashboard')} sx={{ minHeight: 48, px: 3, flex: { xs: 1, md: 'none' } }}>
              {t('wizard.finish')}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default OnboardingWizard;
